import {
  ChangeEvent,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { alpha, darken, lighten, useTheme } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import axios from "axios";
import {
  AutoSizer,
  MultiGrid,
  MultiGridProps,
  SectionRenderedParams,
} from "react-virtualized";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import { LogbookCheck } from "../../../interfaces/Fleet/ILogbookCheck";
import LogbookCheckDialog from "./LogbookCheckDialog";
import { capitalizeFirstChar } from "../../../helpers/formatingFunctions";
import { useSearchParams } from "react-router-dom";

interface Driver {
  id: string;
  firstName: string;
  lastName: string;
  company?: string;
  usedEld?: string;
  assignedTruckUnitNumber?: string;
  assignedTruckDate?: string | null;
  assignedTrailerUnitNumber?: string;
}

const DAY_COLUMN_WIDTH = 54;
const HEADER_HEIGHT = 52;
const ROW_HEIGHT = 96;
const NEW_DRIVER_MONTHS = 1;

const isNewDriverAssignment = (assignedTruckDate?: string | null) => {
  if (!assignedTruckDate) {
    return false;
  }

  const assignmentDate = dayjs(assignedTruckDate);

  if (!assignmentDate.isValid() || assignmentDate.isAfter(dayjs())) {
    return false;
  }

  return dayjs().isBefore(assignmentDate.add(NEW_DRIVER_MONTHS, "month"));
};

const LogbookCheckCalendar = () => {
  const { token } = useAuth();
  const theme = useTheme();
  const compactCalendar = useMediaQuery(theme.breakpoints.down("sm"));
  const FIRST_COLUMN_WIDTH = compactCalendar ? 140 : 300;
  const [searchParams, setSearchParams] = useSearchParams();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [checks, setChecks] = useState<Record<string, LogbookCheck[]>>({});
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf("month"));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(-1);
  const [driverSearch, setDriverSearch] = useState("");
  const [driverInput, setDriverInput] = useState("");
  const [unitSearch, setUnitSearch] = useState("");
  const [visibleRowRange, setVisibleRowRange] = useState({ start: 0, end: 49 });
  const [isLoadingChecks, setIsLoadingChecks] = useState(false);
  const [dialogInfo, setDialogInfo] = useState<{
    open: boolean;
    driverId: string;
    driverName: string;
    dates: Dayjs[];
    existing: (LogbookCheck | null)[];
  }>({ open: false, driverId: "", driverName: "", dates: [], existing: [] });
  const [multiSelection, setMultiSelection] = useState<{
    driverId: string;
    dates: Dayjs[];
  } | null>(null);

  const daysInMonth = currentMonth.daysInMonth();
  const todayKey = dayjs().format("YYYY-MM-DD");

  const headerBackground = useMemo(
    () =>
      `linear-gradient(135deg, ${alpha(
        theme.palette.primary.main,
        0.12,
      )}, ${alpha(theme.palette.secondary.main, 0.12)})`,
    [theme],
  );

  const statusPalette = useMemo(
    () =>
      ({
        none: {
          bg: alpha(lighten(theme.palette.primary.light, 0.45), 0.2),
          border: alpha(theme.palette.primary.main, 0.12),
        },
        complete: {
          bg: alpha(lighten(theme.palette.success.main, 0.18), 0.65),
          border: alpha(darken(theme.palette.success.main, 0.2), 0.8),
        },
        attention: {
          bg: alpha(lighten(theme.palette.warning.main, 0.05), 0.74),
          border: alpha(darken(theme.palette.warning.main, 0.08), 0.8),
        },
        violation: {
          bg: alpha(lighten(theme.palette.error.main, 0.05), 0.82),
          border: alpha(darken(theme.palette.error.main, 0.08), 0.9),
        },
        personalUse: {
          bg: alpha(
            theme.palette.mode === "dark" ? "#f48fb1" : "#f8bbd0",
            0.85,
          ),
          border: alpha(
            theme.palette.mode === "dark" ? "#ec407a" : "#d81b60",
            0.85,
          ),
        },
        dayOff: {
          bg: alpha(lighten(theme.palette.info.main, 0.4), 0.55),
          border: alpha(theme.palette.info.main, 0.5),
        },
      }) as Record<string, LegendSwatch>,
    [theme],
  );

  const {
    none: defaultSwatch,
    complete: completeSwatch,
    attention: attentionSwatch,
    violation: violationSwatch,
    personalUse: personalSwatch,
    dayOff: dayOffSwatch,
  } = statusPalette;

  useEffect(() => {
    if (!token) return;

    setChecks({});

    axios
      .get(`${apiVariable}/getAllDrivers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { areActive: true },
      })
      .then((res) => {
        const samsara = (res.data as Driver[]).filter(
          (driver) => driver.usedEld === "samsara",
        );
        setDrivers(samsara);
      });
  }, [currentMonth, token]);

  useEffect(() => {
    const driverParam = searchParams.get("driver") ?? "";

    setDriverSearch((prev) => (prev === driverParam ? prev : driverParam));
    setDriverInput((prev) => (prev === driverParam ? prev : driverParam));
    setPage(0);
  }, [searchParams]);

  const handleDriverInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setDriverInput(event.target.value);
  };

  const applyDriverSearch = useCallback(
    (rawValue: string) => {
      const normalized = rawValue.trim();

      setDriverSearch((prev) => (prev === normalized ? prev : normalized));
      setDriverInput((prev) => (prev === normalized ? prev : normalized));

      const nextParams = new URLSearchParams(searchParams);

      if (normalized) {
        nextParams.set("driver", normalized);
      } else {
        nextParams.delete("driver");
      }

      if (nextParams.toString() !== searchParams.toString()) {
        setSearchParams(nextParams, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  const handleDriverSearchKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const value = (
        event.currentTarget as HTMLInputElement | HTMLTextAreaElement
      ).value;
      applyDriverSearch(value);
    }
  };

  const checkLookup = useMemo(() => {
    const map: Record<string, Record<string, LogbookCheck>> = {};

    Object.entries(checks).forEach(([driverId, list]) => {
      map[driverId] = {};
      list.forEach((check) => {
        const key = dayjs(check.date).format("YYYY-MM-DD");
        map[driverId][key] = check;
      });
    });

    return map;
  }, [checks]);

  const handlePrevMonth = () => setCurrentMonth((m) => m.subtract(1, "month"));
  const handleNextMonth = () => setCurrentMonth((m) => m.add(1, "month"));

  const handleDialogClose = useCallback(() => {
    setDialogInfo((prev) => ({ ...prev, open: false }));
    setMultiSelection(null);
  }, []);

  const openDialogForCells = useCallback(
    (driver: Driver, dates: Dayjs[]) => {
      const sorted = [...dates].sort((a, b) => a.valueOf() - b.valueOf());
      const existingChecks = sorted.map((date) => {
        const dateKey = date.format("YYYY-MM-DD");
        return checkLookup[driver.id]?.[dateKey] ?? null;
      });

      setDialogInfo({
        open: true,
        driverId: driver.id,
        driverName: `${driver.firstName} ${driver.lastName}`,
        dates: sorted,
        existing: existingChecks,
      });
    },
    [checkLookup],
  );

  const toggleSelection = useCallback((driverId: string, date: Dayjs) => {
    setMultiSelection((prev) => {
      const normalizedDate = date.startOf("day");

      if (!prev || prev.driverId !== driverId) {
        return { driverId, dates: [normalizedDate] };
      }

      const exists = prev.dates.some((d) => d.isSame(normalizedDate, "day"));
      if (exists) {
        const remaining = prev.dates.filter(
          (d) => !d.isSame(normalizedDate, "day"),
        );
        if (remaining.length === 0) {
          return null;
        }
        return { driverId, dates: remaining };
      }

      return { driverId, dates: [...prev.dates, normalizedDate] };
    });
  }, []);

  const handleCellClick = useCallback(
    (event: MouseEvent<HTMLDivElement>, driver: Driver, date: Dayjs) => {
      const isModifier = event.shiftKey || event.metaKey || event.ctrlKey;

      if (isModifier) {
        toggleSelection(driver.id, date);
        return;
      }

      const isCurrentDriverSelected = multiSelection?.driverId === driver.id;
      const isDateSelected = multiSelection?.dates.some((d) =>
        d.isSame(date, "day"),
      );

      if (
        isCurrentDriverSelected &&
        multiSelection &&
        multiSelection.dates.length > 1 &&
        isDateSelected
      ) {
        openDialogForCells(driver, multiSelection.dates);
        return;
      }

      const normalizedDate = date.startOf("day");
      setMultiSelection({ driverId: driver.id, dates: [normalizedDate] });
      openDialogForCells(driver, [normalizedDate]);
    },
    [multiSelection, openDialogForCells, toggleSelection],
  );

  const days = useMemo(
    () => Array.from({ length: daysInMonth }, (_, index) => index + 1),
    [daysInMonth],
  );

  const selectedKeySet = useMemo(() => {
    if (!multiSelection) {
      return new Set<string>();
    }

    return new Set(
      multiSelection.dates.map(
        (date) => `${multiSelection.driverId}_${date.format("YYYY-MM-DD")}`,
      ),
    );
  }, [multiSelection]);

  const handleSaved = (check: LogbookCheck | LogbookCheck[]) => {
    const items = Array.isArray(check) ? check : [check];

    setChecks((prev) => {
      const updated = { ...prev };

      items.forEach((item) => {
        const driverKey = item.driverId || dialogInfo.driverId;
        const list = updated[driverKey] || [];
        const existingIndex = list.findIndex((c) => c.id === item.id);
        let nextList: LogbookCheck[];

        if (existingIndex >= 0) {
          nextList = [...list];
          nextList[existingIndex] = item;
        } else {
          const filtered = list.filter(
            (c) => !dayjs(c.date).isSame(item.date, "day"),
          );
          nextList = [...filtered, item];
        }

        updated[driverKey] = nextList;
      });

      return updated;
    });

    setMultiSelection(null);
    setDialogInfo((prev) => ({ ...prev, open: false }));
  };

  const filteredDrivers = useMemo(() => {
    const driverQuery = driverSearch.trim().toLowerCase();
    const unitQuery = unitSearch.trim().toLowerCase();

    if (!driverQuery && !unitQuery) {
      return drivers;
    }

    return drivers.filter((driver) => {
      const name = `${driver.firstName} ${driver.lastName}`.toLowerCase();
      const company = driver.company?.toLowerCase() ?? "";
      const matchesDriver =
        !driverQuery ||
        name.includes(driverQuery) ||
        company.includes(driverQuery);

      if (!matchesDriver) {
        return false;
      }

      if (!unitQuery) {
        return true;
      }

      const pool = [
        driver.assignedTruckUnitNumber,
        driver.assignedTrailerUnitNumber,
      ]
        .filter((value): value is string => !!value)
        .map((value) => value.toLowerCase());

      if (pool.length === 0) {
        return false;
      }

      return pool.some((value) => value.includes(unitQuery));
    });
  }, [driverSearch, drivers, unitSearch]);

  useEffect(() => {
    setPage(0);
  }, [driverSearch, unitSearch]);

  useEffect(() => {
    setVisibleRowRange((prev) => {
      if (rowsPerPage === -1) {
        const endCap = Math.max(filteredDrivers.length - 1, -1);
        const nextEnd = Math.min(Math.max(prev.end, 49), endCap);
        const nextStart = Math.min(
          Math.max(prev.start, 0),
          Math.max(nextEnd, 0),
        );

        return { start: nextStart, end: nextEnd };
      }

      const cappedEnd = Math.min(filteredDrivers.length - 1, rowsPerPage - 1);
      return { start: 0, end: Math.max(cappedEnd, -1) };
    });
  }, [filteredDrivers.length, rowsPerPage]);

  useEffect(() => {
    if (rowsPerPage === -1) return;

    const maxPage = Math.max(
      0,
      Math.ceil(filteredDrivers.length / rowsPerPage) - 1,
    );

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredDrivers.length, rowsPerPage, page]);

  const displayedDrivers = useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredDrivers;
    }

    const start = page * rowsPerPage;
    return filteredDrivers.slice(start, start + rowsPerPage);
  }, [filteredDrivers, page, rowsPerPage]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
  };

  const handleSectionRendered = useCallback(
    ({ rowStartIndex, rowStopIndex }: SectionRenderedParams) => {
      if (rowsPerPage !== -1) return;

      const start = Math.max(rowStartIndex - 1, 0);
      const end = Math.max(rowStopIndex - 1, 0);

      setVisibleRowRange((prev) =>
        prev.start === start && prev.end === end ? prev : { start, end },
      );
    },
    [rowsPerPage],
  );

  const fetchWindow = useMemo(() => {
    if (rowsPerPage === -1) {
      return {
        start: visibleRowRange.start,
        end: Math.min(visibleRowRange.end, filteredDrivers.length - 1),
      };
    }

    const start = page * rowsPerPage;
    return {
      start,
      end: Math.min(filteredDrivers.length - 1, start + rowsPerPage - 1),
    };
  }, [
    filteredDrivers.length,
    page,
    rowsPerPage,
    visibleRowRange.end,
    visibleRowRange.start,
  ]);

  useEffect(() => {
    if (!token || drivers.length === 0) {
      setIsLoadingChecks(false);
      return;
    }

    const month = currentMonth.month() + 1;
    const year = currentMonth.year();
    const { start, end } = fetchWindow;

    if (end < start || end < 0) {
      setIsLoadingChecks(false);
      return;
    }

    const driversToFetch = filteredDrivers
      .slice(start, end + 1)
      .filter((driver) => !checks[driver.id]);

    if (driversToFetch.length === 0) {
      setIsLoadingChecks(false);
      return;
    }

    let isCancelled = false;

    setIsLoadingChecks(true);

    Promise.all(
      driversToFetch.map((driver) =>
        axios
          .get(`${apiVariable}/logbookChecks/driver/${driver.id}`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { month, year },
          })
          .then((r) => ({ driverId: driver.id, data: r.data })),
      ),
    )
      .then((results) => {
        if (isCancelled) return;

        setChecks((prev) => {
          const next = { ...prev };
          results.forEach(({ driverId, data }) => {
            next[driverId] = data;
          });
          return next;
        });
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingChecks(false);
        }
      });

    return () => {
      isCancelled = true;
      setIsLoadingChecks(false);
    };
  }, [
    checks,
    currentMonth,
    drivers.length,
    fetchWindow,
    filteredDrivers,
    token,
  ]);

  const renderGridCell = useCallback<MultiGridProps["cellRenderer"]>(
    ({ columnIndex, key, rowIndex, style }) => {
      if (rowIndex === 0) {
        if (columnIndex === 0) {
          return (
            <Box
              key={key}
              style={style}
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                bgcolor: headerBackground,
                borderBottom: 1,
                borderColor: alpha(theme.palette.divider, 0.6),
                fontWeight: 600,
                borderRight: 1,
                color: "text.primary",
                letterSpacing: 0.2,
                textTransform: "uppercase",
                fontSize: 12,
                zIndex: 4,
              }}
            >
              Driver Overview
            </Box>
          );
        }

        const dayHeader = days[columnIndex - 1];
        const date = currentMonth.date(dayHeader);
        return (
          <Box
            key={key}
            style={style}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: headerBackground,
              borderBottom: 1,
              borderColor: alpha(theme.palette.divider, 0.6),
              borderRight: 1,
              color: "text.primary",
              fontWeight: 600,
              fontSize: 12,
              zIndex: 3,
            }}
          >
            <Stack spacing={0} alignItems="center">
              <Typography fontSize={12} lineHeight={1} fontWeight={700}>
                {dayHeader}
              </Typography>
              <Typography fontSize={10} color="text.secondary">
                {date.format("ddd")}
              </Typography>
            </Stack>
          </Box>
        );
      }

      const driver = displayedDrivers[rowIndex - 1];
      if (!driver) {
        return null;
      }

      if (columnIndex === 0) {
        if (compactCalendar) {
          return (
            <Box
              key={key}
              style={style}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 0.5,
                px: 1,
                overflow: "hidden",
                bgcolor: "background.paper",
                borderBottom: 1,
                borderRight: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                title={`${driver.firstName} ${driver.lastName}`}
                variant="body2"
                fontWeight={700}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  overflowWrap: "anywhere",
                }}
              >
                {driver.firstName} {driver.lastName}
              </Typography>
              <Typography variant="caption">
                Truck: {driver.assignedTruckUnitNumber || "Unassigned"} ·
                Trailer: {driver.assignedTrailerUnitNumber || "Unassigned"}
              </Typography>
            </Box>
          );
        }
        return (
          <Box
            key={key}
            style={style}
            sx={{
              display: "flex",
              alignItems: "stretch",
              bgcolor: alpha(theme.palette.background.paper, 0.92),
              borderBottom: 1,
              borderColor: alpha(theme.palette.divider, 0.6),
              borderRight: 1,
              px: 2,
              py: 1.5,
              zIndex: 2,
            }}
          >
            <Stack spacing={0.75} sx={{ overflow: "hidden" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "14px",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                  }}
                >
                  {driver.firstName.charAt(0)}
                  {driver.lastName.charAt(0)}
                </Box>
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      letterSpacing={0.2}
                      noWrap
                      sx={{ minWidth: 0 }}
                    >
                      {driver.firstName} {driver.lastName}
                    </Typography>
                    {isNewDriverAssignment(driver.assignedTruckDate) && (
                      <Tooltip
                        title={`Assigned to truck ${dayjs(
                          driver.assignedTruckDate,
                        ).format("MMM D, YYYY")}`}
                        arrow
                      >
                        <Chip
                          size="small"
                          label="New"
                          color="warning"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 700,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.warning.main, 0.18),
                            border: `1px solid ${alpha(
                              theme.palette.warning.main,
                              0.5,
                            )}`,
                            color:
                              theme.palette.mode === "dark"
                                ? theme.palette.warning.light
                                : theme.palette.warning.dark,
                            flexShrink: 0,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    ELD Platform:{" "}
                    {capitalizeFirstChar(driver.usedEld || "Unknown")}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap">
                <Chip
                  size="small"
                  variant="outlined"
                  label={driver.assignedTruckUnitNumber || "No Truck"}
                  color={driver.assignedTruckUnitNumber ? "primary" : undefined}
                  sx={{ height: 22, fontSize: 11, borderRadius: 1 }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={driver.assignedTrailerUnitNumber || "No Trailer"}
                  color={
                    driver.assignedTrailerUnitNumber ? "secondary" : undefined
                  }
                  sx={{ height: 22, fontSize: 11, borderRadius: 1 }}
                />
                <Tooltip
                  title={
                    driver.company ? `Company: ${driver.company}` : "No company"
                  }
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={driver.company || "No Company"}
                    color={driver.company ? "info" : undefined}
                    sx={{ height: 22, fontSize: 11, borderRadius: 1 }}
                  />
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        );
      }

      const day = days[columnIndex - 1];
      const date = currentMonth.date(day);
      const dateKey = date.format("YYYY-MM-DD");
      const lookup = checkLookup[driver.id];
      const existing = lookup?.[dateKey];
      const tooltip = existing ? (
        <Stack spacing={0.75} sx={{ p: 0.5 }}>
          <Typography variant="caption" fontWeight={700}>
            {existing.status.color}
          </Typography>
          {existing.status.statuses.map((status) => (
            <StatusLine
              key={status.label}
              label={status.label}
              value={status.value}
              swatch={statusPalette[existing.status.key] ?? defaultSwatch}
            />
          ))}
        </Stack>
      ) : (
        "No check"
      );

      const swatch = existing
        ? (statusPalette[existing.status.key] ?? defaultSwatch)
        : defaultSwatch;

      const isToday = dateKey === todayKey;
      const isSelected = selectedKeySet.has(`${driver.id}_${dateKey}`);

      return (
        <Box
          key={key}
          style={style}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: 1,
            borderRight: 1,
            borderColor: alpha(theme.palette.divider, 0.5),
            bgcolor: alpha(theme.palette.background.paper, 0.92),
            px: 0.5,
          }}
        >
          <Tooltip title={tooltip} arrow enterDelay={300}>
            <Box
              role="button"
              tabIndex={0}
              data-calendar-cell
              aria-label={`Edit check for ${driver.firstName} ${driver.lastName} on ${date.format("MMMM D, YYYY")}`}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openDialogForCells(driver, [date.startOf("day")]);
                }
              }}
              onClick={(event) => handleCellClick(event, driver, date)}
              sx={{
                width: "100%",
                height: ROW_HEIGHT - 36,
                cursor: "pointer",
                bgcolor: swatch.bg,
                border: isToday || isSelected ? 2 : 1,
                borderColor: isSelected
                  ? theme.palette.secondary.main
                  : isToday
                    ? theme.palette.primary.main
                    : swatch.border,
                borderRadius: 1,
                boxShadow: isSelected
                  ? `0 10px 22px ${alpha(theme.palette.secondary.main, 0.25)}`
                  : isToday
                    ? `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`
                    : "none",
                transition:
                  "box-shadow 160ms ease, transform 160ms ease, border-color 160ms ease",
                "&:hover": {
                  boxShadow: `0 10px 24px ${alpha(
                    theme.palette.grey[900],
                    0.12,
                  )}`,
                  transform: "translateY(-2px)",
                },
              }}
            />
          </Tooltip>
        </Box>
      );
    },
    [
      compactCalendar,
      checkLookup,
      currentMonth,
      days,
      displayedDrivers,
      handleCellClick,
      openDialogForCells,
      headerBackground,
      attentionSwatch,
      completeSwatch,
      dayOffSwatch,
      defaultSwatch,
      selectedKeySet,
      personalSwatch,
      violationSwatch,
      theme,
      todayKey,
      statusPalette,
    ],
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 3 },
        borderRadius: 3,
        bgcolor: alpha(theme.palette.background.paper, 0.95),
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.08)}`,
      }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent={{ xs: "flex-start", md: "space-between" }}
        >
          <Stack spacing={1.5}>
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              Logbook Compliance
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Previous month" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handlePrevMonth}
                  sx={{
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.24,
                    )}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.16),
                    },
                  }}
                >
                  <ArrowBackIosNewRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  minWidth: { xs: 0, sm: 180 },
                }}
              >
                {currentMonth.format("MMMM YYYY")}
              </Typography>
              <Tooltip title="Next month" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleNextMonth}
                  sx={{
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.24,
                    )}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.16),
                    },
                  }}
                >
                  <ArrowForwardIosRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
        <Stack
          direction={{ xs: "column", xl: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", xl: "center" }}
          justifyContent="space-between"
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              value={driverInput}
              onChange={handleDriverInputChange}
              onKeyDown={handleDriverSearchKeyDown}
              onBlur={() => applyDriverSearch(driverInput)}
              size="small"
              placeholder="Search driver"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size="small"
                      edge="start"
                      aria-label="search drivers"
                      onClick={() => applyDriverSearch(driverInput)}
                    >
                      <SearchRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: "100%", sm: 220 },
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                borderRadius: 2,
              }}
            />
            <TextField
              value={unitSearch}
              onChange={(event) => setUnitSearch(event.target.value)}
              size="small"
              placeholder="Search truck / trailer"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: "100%", sm: 240 },
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                borderRadius: 2,
              }}
            />
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            <LegendItem swatch={completeSwatch} label="All Good" />
            <LegendItem swatch={attentionSwatch} label="Needs Attention" />
            <LegendItem swatch={violationSwatch} label="Violation" />
            <LegendItem swatch={personalSwatch} label="Over Personal Use" />
            <LegendItem swatch={dayOffSwatch} label="Day Off" />
            <LegendItem swatch={defaultSwatch} label="No Check" />
          </Stack>
        </Stack>
        <Divider flexItem sx={{ opacity: 0.6 }} />
      </Stack>
      <Box
        role="region"
        aria-label="Logbook check calendar"
        sx={{
          mt: 2,
          height: 620,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          overflow: "hidden",
          backdropFilter: "blur(4px)",
          position: "relative",
        }}
      >
        {isLoadingChecks && (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={2}
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: alpha(theme.palette.background.paper, 0.65),
              zIndex: 2,
            }}
          >
            <CircularProgress color="primary" />
            <Typography variant="body2" color="text.secondary">
              Loading logbook checks...
            </Typography>
          </Stack>
        )}
        <AutoSizer>
          {({ height, width }) => (
            <MultiGrid
              key={compactCalendar ? "compact" : "desktop"}
              cellRenderer={renderGridCell}
              columnCount={days.length + 1}
              columnWidth={({ index }) =>
                index === 0 ? FIRST_COLUMN_WIDTH : DAY_COLUMN_WIDTH
              }
              fixedColumnCount={1}
              fixedRowCount={1}
              height={height}
              rowCount={displayedDrivers.length + 1}
              rowHeight={({ index }) =>
                index === 0 ? HEADER_HEIGHT : ROW_HEIGHT
              }
              overscanColumnCount={2}
              overscanRowCount={5}
              onSectionRendered={handleSectionRendered}
              style={{ border: "none" }}
              styleBottomLeftGrid={{
                backgroundColor: alpha(theme.palette.background.paper, 0.96),
              }}
              styleBottomRightGrid={{
                backgroundColor: alpha(theme.palette.background.paper, 0.96),
              }}
              styleTopLeftGrid={{
                backgroundColor: headerBackground,
              }}
              styleTopRightGrid={{
                backgroundColor: headerBackground,
              }}
              width={width}
            />
          )}
        </AutoSizer>
      </Box>
      <TablePagination
        component="div"
        count={filteredDrivers.length}
        page={rowsPerPage === -1 ? 0 : page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[{ label: "All", value: -1 }, 25, 50, 100]}
        labelRowsPerPage="Drivers per view"
        labelDisplayedRows={({ from, to, count }) =>
          rowsPerPage === -1 ? `${count} drivers` : `${from}–${to} of ${count}`
        }
        sx={{
          mt: 1,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
          ".MuiTablePagination-toolbar": {
            px: { xs: 0, sm: 2 },
          },
        }}
      />
      <LogbookCheckDialog
        open={dialogInfo.open}
        onClose={handleDialogClose}
        driverId={dialogInfo.driverId}
        driverName={dialogInfo.driverName}
        dates={dialogInfo.dates}
        existing={dialogInfo.existing}
        onSaved={handleSaved}
      />
    </Paper>
  );
};

interface LegendSwatch {
  bg: string;
  border: string;
}

const LegendItem = ({
  swatch,
  label,
}: {
  swatch: LegendSwatch;
  label: string;
}) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box
      sx={{
        width: 16,
        height: 16,
        bgcolor: swatch.bg,
        borderRadius: 1,
        border: `1px solid ${swatch.border}`,
      }}
    />
    <Typography variant="body2" color="text.secondary" fontWeight={500}>
      {label}
    </Typography>
  </Stack>
);

const StatusLine = ({
  label,
  value,
  swatch,
}: {
  label: string;
  value: boolean;
  swatch?: LegendSwatch;
}) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Typography variant="caption" sx={{ minWidth: 150 }}>
      {label}:
    </Typography>
    <Chip
      size="small"
      label={value ? "Yes" : "No"}
      color={value ? undefined : "error"}
      variant={value ? "outlined" : "filled"}
      sx={{
        height: 20,
        fontSize: 11,
        px: 0.75,
        borderRadius: 1,
        fontWeight: 600,
        bgcolor: value && swatch ? swatch.bg : undefined,
        border: value && swatch ? `1px solid ${swatch.border}` : undefined,
      }}
    />
  </Stack>
);

export default LogbookCheckCalendar;
